import { createApp } from "vue";
import 'primeicons/primeicons.css'
import "./assets/style.css";

import * as Sentry from "@sentry/vue";
import App from "./App.vue";
import VueSmoothScroll from "vue3-smooth-scroll";
import LinkButton from "./components/LinkButton.vue";
import router from "./routes/index.js";
import {createPinia} from 'pinia'
import { setupI18n, getLocaleFromPath, setI18nLanguage } from './i18n/index.js';

import PrimeVue from "primevue/config";
import ConfirmationService from 'primevue/confirmationservice';
import Tooltip from 'primevue/tooltip';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faColumns } from "@fortawesome/free-solid-svg-icons";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { faUsers } from "@fortawesome/free-solid-svg-icons";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { faHouse } from "@fortawesome/free-solid-svg-icons";
import { faAngleDoubleLeft }  from "@fortawesome/free-solid-svg-icons";
import { faListDots } from "@fortawesome/free-solid-svg-icons";
import Noir from "./assets/Noir.js";
import ToastService from 'primevue/toastservice';
import Dialog from 'primevue/dialog';
import Toast from "primevue/toast";
import Popover from "primevue/popover";
import vue3ToPdf from 'vue3-to-pdf'
import vueBoxShadows from "vue-box-shadows";
import { createHead } from '@unhead/vue'

// Add icons to FontAwesome library
library.add(faColumns);
library.add(faChartBar);
library.add(faUsers);
library.add(faImage);
library.add(faHouse);
library.add(faAngleDoubleLeft);
library.add(faListDots);
library.add(faColumns, faChartBar, faUsers, faImage, faHouse, faAngleDoubleLeft);

const pinia = createPinia();
const i18n = setupI18n();
// Force locale from URL on initial load (hard refresh support)
// This MUST run synchronously before any stores initialize
const initialLocale = getLocaleFromPath(window.location.pathname);
i18n.global.locale.value = initialLocale;
localStorage.setItem('user-locale', initialLocale);
const head = createHead()

let app = createApp(App);

// Disable Sentry in local development
if (!import.meta.env.DEV) {
  Sentry.init({
    app,
    dsn: "https://ab4524777861eb76e99d4ab6dfc7e704@o4508852502790144.ingest.de.sentry.io/4508852505083984",
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", "idou.ai", /^\//],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  // Global window error handlers for non-Vue errors
  window.onerror = (message, source, lineno, colno, error) => {
    Sentry.captureException(error || new Error(String(message)), {
      extra: { source, lineno, colno },
    });
  };

  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason || new Error('Unhandled Promise Rejection'), {
      extra: { type: 'unhandledrejection' },
    });
  });
}

  
app.use(pinia);
app.use(router);
app.use(i18n);
app.use(PrimeVue, {theme: {
        preset: Noir,
        options: {
            prefix: 'p',
            darkModeSelector: '.p-dark',
            cssLayer: false,
        }
    }
});
app.use(vueBoxShadows, {
  useClass: true
});
app.use(VueSmoothScroll);
app.use(head);

app.use(ToastService);
app.use(ConfirmationService);

// Global error handler for Vue component errors
app.config.errorHandler = (err, vm, info) => {
  if (!import.meta.env.DEV) {
    Sentry.captureException(err, {
      extra: {
        componentName: vm?.$options?.name || 'UnknownComponent',
        componentInfo: info,
        route: vm?.$route?.path,
      },
    });
  }
  console.error('Vue Error:', err, 'Info:', info);
};
app.use(Dialog);
app.use(vue3ToPdf);
app.directive('tooltip', Tooltip);
app.component("LinkButton", LinkButton);
app.component("font-awesome-icon", FontAwesomeIcon)
app.component("Toast", Toast);
app.component('Popover', Popover);



app.mount("#app");
